import React from 'react';

export default function AIWork() {
  const aiProjects = [
    {
      id: 1,
      title: 'AI-Powered Release Note Generation Service',
      description: 'Built an intelligent release-note generation service seamlessly integrated into the CI/CD pipeline using AWS Bedrock and Claude.',
      impact: '97% reduction in manual creation effort',
      details: [
        'Automated extraction of commits tagged with "feat" or "fix" from release pipeline',
        'Intelligent filtering and categorization of commits using Claude AI',
        'Generated polished, external-facing release notes with proper grouping and formatting',
        'Automated deployment to developer.bill.com as interactive webpages',
        'Slack integration for instant team notifications',
      ],
      tech: ['AWS Bedrock', 'Claude API', 'CI/CD Pipeline', 'Python', 'Slack Integration'],
      focus: 'AI Automation',
    },
    {
      id: 2,
      title: 'E2E Testing Summarization Workflow',
      description: 'Developed an AI-driven nightly E2E testing summarization system that processes test results and generates actionable insights.',
      impact: '45 minutes → 5 minutes (on-call daily investigation time)',
      details: [
        'Automated comparison of current vs previous night E2E test results',
        'Intelligent detection of persistently failing tests and net new failures',
        'Claude-powered structured analysis generating high-signal insights from raw test output',
        'Real-time processing within nightly pipeline execution',
        'Drastically reduced on-call engineer investigation and triage time',
      ],
      tech: ['AWS Bedrock', 'Claude API', 'Test Automation', 'Data Processing', 'Python'],
      focus: 'AI Analytics',
    },
  ];

  const featuredWork = [
    {
      id: 3,
      title: 'Vendors Embeddable Widget Architecture',
      description: 'Architected and led development of the Vendors Embeddable Widget, enabling partner platforms to manage vendors across domestic and international markets.',
      impact: 'Scalable API design enabling partner integration',
      details: [
        'Collaborated with backend, platform, and international payment teams',
        'Designed scalable, flexible APIs for vendor creation and updates',
        'Established integration patterns for partner platform adoption',
        'Supported both domestic and international vendor operations',
        'Enabled partners to embed vendor management directly in their applications',
      ],
      tech: ['API Design', 'Microservices', 'Integration Architecture', 'Payment Systems'],
      focus: 'System Design',
    },
    {
      id: 4,
      title: 'International Payments Experience',
      description: 'Designed and delivered the international payments experience within the scheduling payments system supporting global transactions.',
      impact: 'Global transaction support with complex validation',
      details: [
        'Integrated geolocation-driven payment logic across services',
        'Collaborated with API teams to ensure seamless data flow',
        'Implemented complex validation flows for international compliance',
        'Designed user experience for global payment scenarios',
        'Coordinated across multiple service domains for transaction support',
      ],
      tech: ['Payment Systems', 'Geolocation Logic', 'API Integration', 'Compliance'],
      focus: 'Payment Systems',
    },
  ];

  return (
    <>
      

      {/* AI Work Section */}
      <section id="ai-work" className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-4 text-gray-900">AI & Automation Work</h2>
          <p className="text-lg text-gray-600 mb-12">Leveraging AI to solve complex problems and scale engineering operations</p>
          
          <div className="grid grid-cols-1 gap-8">
            {aiProjects.map((project) => (
              <div key={project.id} className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg shadow-lg hover:shadow-xl transition p-8 border-l-4 border-purple-600">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{project.title}</h3>
                    <p className="text-gray-700 text-lg mb-3">{project.description}</p>
                  </div>
                  <span className="bg-purple-600 text-white px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap ml-4">
                    {project.focus}
                  </span>
                </div>
                
                {/* Impact Badge */}
                <div className="mb-6 p-4 bg-white rounded-lg border border-purple-200">
                  <p className="text-sm font-semibold text-purple-600 uppercase tracking-wide">Impact</p>
                  <p className="text-lg font-bold text-gray-900">{project.impact}</p>
                </div>

                {/* Details */}
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-900 mb-3">Key Accomplishments</h4>
                  <ul className="space-y-2">
                    {project.details.map((detail, idx) => (
                      <li key={idx} className="flex items-start text-gray-700">
                        <span className="w-2 h-2 bg-purple-600 rounded-full mr-3 mt-2 flex-shrink-0"></span>
                        <span>{detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Technologies */}
                <div className="flex flex-wrap gap-2">
                  {project.tech.map((tech, idx) => (
                    <span key={idx} className="inline-block bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm font-semibold">
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Work Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-4 text-gray-900">Featured Work</h2>
          <p className="text-lg text-gray-600 mb-12">Architecting scalable systems and leading cross-functional initiatives</p>
          
          <div className="grid grid-cols-1 gap-8">
            {featuredWork.map((project) => (
              <div key={project.id} className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg shadow-lg hover:shadow-xl transition p-8 border-l-4 border-blue-600">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{project.title}</h3>
                    <p className="text-gray-700 text-lg mb-3">{project.description}</p>
                  </div>
                  <span className="bg-blue-600 text-white px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap ml-4">
                    {project.focus}
                  </span>
                </div>
                
                {/* Impact Badge */}
                <div className="mb-6 p-4 bg-white rounded-lg border border-blue-200">
                  <p className="text-sm font-semibold text-blue-600 uppercase tracking-wide">Impact</p>
                  <p className="text-lg font-bold text-gray-900">{project.impact}</p>
                </div>

                {/* Details */}
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-900 mb-3">Key Accomplishments</h4>
                  <ul className="space-y-2">
                    {project.details.map((detail, idx) => (
                      <li key={idx} className="flex items-start text-gray-700">
                        <span className="w-2 h-2 bg-blue-600 rounded-full mr-3 mt-2 flex-shrink-0"></span>
                        <span>{detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Technologies */}
                <div className="flex flex-wrap gap-2">
                  {project.tech.map((tech, idx) => (
                    <span key={idx} className="inline-block bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold">
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
